import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import {
  useCompanyInfo,
  useUpdateWhatsAppCloudConfig,
  useConfiguredSecrets,
  useSetSecret,
  useUnsetSecret,
} from '@/hooks/useSettings';
import { BadgeCheck, KeyRound, AlertCircle, Info, Check } from 'lucide-react';

interface FormValues {
  phoneNumberId: string;
  templateName: string;
  templateLanguage: string;
  deliveryMode: 'link' | 'pdf';
  pdfTemplateName: string;
  pdfTemplateLanguage: string;
  enabled: boolean;
}

export function WhatsAppCloudConfigForm() {
  const { data: company, isLoading } = useCompanyInfo();
  const update = useUpdateWhatsAppCloudConfig();

  const { data: secrets } = useConfiguredSecrets();
  const setToken = useSetSecret();
  const unsetToken = useUnsetSecret();
  const [tokenDraft, setTokenDraft] = useState('');
  const [tokenMsg, setTokenMsg] = useState<string | null>(null);
  const hasToken = (secrets ?? []).includes('WHATSAPP_ACCESS_TOKEN');

  const saveToken = async () => {
    setTokenMsg(null);
    try {
      await setToken.mutateAsync({
        key: 'WHATSAPP_ACCESS_TOKEN',
        value: tokenDraft.trim(),
      });
      // Clear immediately: the value is write-only and must not linger in a
      // form field where the next person at this terminal could read it.
      setTokenDraft('');
      setTokenMsg('Token tersimpan.');
    } catch (e) {
      setTokenMsg(e instanceof Error ? e.message : 'Gagal menyimpan token');
    }
  };

  const removeToken = async () => {
    setTokenMsg(null);
    try {
      await unsetToken.mutateAsync('WHATSAPP_ACCESS_TOKEN');
      setTokenMsg('Token dihapus.');
    } catch (e) {
      setTokenMsg(e instanceof Error ? e.message : 'Gagal menghapus token');
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      phoneNumberId: '',
      templateName: 'invoice_ready',
      templateLanguage: 'id',
      deliveryMode: 'link',
      pdfTemplateName: 'invoice_pdf',
      pdfTemplateLanguage: 'id',
      enabled: false,
    },
  });

  useEffect(() => {
    if (company) {
      reset({
        phoneNumberId: company.whatsappCloud?.phoneNumberId ?? '',
        templateName: company.whatsappCloud?.templateName ?? 'invoice_ready',
        templateLanguage: company.whatsappCloud?.templateLanguage ?? 'id',
        deliveryMode: company.whatsappCloud?.deliveryMode ?? 'link',
        pdfTemplateName: company.whatsappCloud?.pdfTemplateName ?? 'invoice_pdf',
        pdfTemplateLanguage:
          company.whatsappCloud?.pdfTemplateLanguage
          ?? company.whatsappCloud?.templateLanguage ?? 'id',
        enabled: company.whatsappCloud?.enabled ?? false,
      });
    }
  }, [company, reset]);

  const onSubmit = async (values: FormValues) => {
    await update.mutateAsync({
      phoneNumberId: values.phoneNumberId.trim(),
      templateName: values.templateName.trim(),
      templateLanguage: values.templateLanguage.trim() || 'id',
      deliveryMode: values.deliveryMode,
      pdfTemplateName: values.pdfTemplateName.trim() || 'invoice_pdf',
      pdfTemplateLanguage: values.pdfTemplateLanguage.trim() || 'id',
      enabled: values.enabled,
    });
  };

  const enabled = watch('enabled');
  const phoneNumberId = watch('phoneNumberId');
  const deliveryMode = watch('deliveryMode');

  const shadowed = [
    company?.n8n?.enabled ? 'n8n' : null,
    company?.hermes?.enabled ? 'Hermes' : null,
    company?.openclaw?.enabled ? 'OpenClaw' : null,
  ].filter(Boolean) as string[];

  return (
    <Card>
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-success/10 text-success flex items-center justify-center flex-shrink-0">
          <BadgeCheck size={18} />
        </div>
        <div className="flex-1">
          <CardTitle>WhatsApp Cloud API (resmi)</CardTitle>
          <CardDescription>
            Gateway resmi dari Meta. Tidak perlu VPS, tidak perlu scan QR, dan
            tidak ada risiko nomor kena banned seperti Baileys. Setup lengkap
            ada di <code className="font-mono text-caption">WHATSAPP_CLOUD_API.md</code>.
          </CardDescription>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <Chip variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Aktif' : 'Nonaktif'}
        </Chip>
        <span className="text-caption text-ink-subtle">
          {enabled
            ? 'Invoice baru dikirim via Cloud API resmi'
            : 'Cloud API tidak dipakai'}
        </span>
      </div>

      {/* Access token. Stored in integration_secrets via the owner-only
          admin-secrets function — never in company_info, which every cashier's
          browser downloads. Write-only: there is no read-back, so the field
          shows "tersimpan" rather than the value. */}
      <div className="mb-5 p-4 rounded bg-canvas/40 border border-line">
        <div className="flex items-start gap-2 mb-3">
          <KeyRound size={16} className="mt-0.5 flex-shrink-0 text-ink-subtle" />
          <div className="flex-1">
            <p className="text-small font-medium text-ink">Access token</p>
            <p className="text-caption text-ink-subtle mt-0.5">
              Token System User dari Meta. Disimpan terenkripsi di server dan
              hanya bisa dibaca edge function — tidak pernah ikut terunduh ke
              browser kasir, dan tidak bisa dilihat lagi setelah disimpan.
            </p>
          </div>
          {hasToken && (
            <Chip variant="success">
              <Check size={12} className="inline mr-1" />
              Tersimpan
            </Chip>
          )}
        </div>

        <div className="flex gap-2 items-start">
          <div className="flex-1">
            <Input
              type="password"
              autoComplete="off"
              placeholder={hasToken ? 'Isi untuk mengganti token' : 'EAAG…'}
              value={tokenDraft}
              onChange={(e) => setTokenDraft(e.target.value)}
              hint={
                hasToken
                  ? 'Sudah ada token. Mengisi ini akan menimpa yang lama.'
                  : 'Business settings → System users → Generate new token.'
              }
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={saveToken}
            loading={setToken.isPending}
            disabled={!tokenDraft.trim()}
          >
            Simpan token
          </Button>
          {hasToken && (
            <Button
              type="button"
              variant="ghost"
              onClick={removeToken}
              loading={unsetToken.isPending}
            >
              Hapus
            </Button>
          )}
        </div>

        {tokenMsg && (
          <p className="mt-2 text-caption text-ink-subtle">{tokenMsg}</p>
        )}
      </div>

      {enabled && !hasToken && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded bg-warning/5 border border-warning/30 text-small text-warning">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            Cloud API aktif tapi access token belum diisi — setiap invoice akan
            gagal terkirim dengan error <i>missing token</i>.
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Toggle
          checked={enabled}
          onChange={(v) => setValue('enabled', v, { shouldDirty: true })}
          label="Aktifkan WhatsApp Cloud API"
          description="Aktifkan setelah template disetujui Meta dan secret token sudah di-set."
        />

        <Input
          label="Phone Number ID"
          placeholder="123456789012345"
          hint="Dari WhatsApp Manager → API Setup. Ini ID numerik, BUKAN nomor HP-nya."
          error={errors.phoneNumberId?.message}
          {...register('phoneNumberId', {
            validate: (v) => {
              if (enabled && !v.trim()) return 'Phone Number ID wajib diisi kalau aktif';
              if (v.trim() && !/^\d+$/.test(v.trim())) return 'Harus angka semua';
              return true;
            },
          })}
          disabled={isLoading}
        />

        {/* Two approved templates can coexist: switching is a setting, not a
            deploy, so the one already approved keeps working while the other
            is being prepared. */}
        <fieldset className="space-y-2">
          <legend className="text-small font-medium text-ink">Yang dikirim ke customer</legend>
          <div className="grid sm:grid-cols-2 gap-2">
            <ModeCard
              active={deliveryMode === 'link'}
              onClick={() => setValue('deliveryMode', 'link', { shouldDirty: true })}
              title="Link invoice"
              description="Tombol ke halaman invoice. Customer masukkan 4 digit terakhir no HP untuk membuka."
            />
            <ModeCard
              active={deliveryMode === 'pdf'}
              onClick={() => setValue('deliveryMode', 'pdf', { shouldDirty: true })}
              title="PDF langsung"
              description="File invoice menempel di chat. Tidak ada password, customer tinggal simpan."
            />
          </div>
        </fieldset>

        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            label={deliveryMode === 'pdf' ? 'Nama template PDF' : 'Nama template link'}
            placeholder={deliveryMode === 'pdf' ? 'invoice_pdf' : 'invoice_ready'}
            hint="Nama template yang sudah approved di Meta."
            error={errors.templateName?.message ?? errors.pdfTemplateName?.message}
            {...(deliveryMode === 'pdf'
              ? register('pdfTemplateName', {
                  validate: (v) =>
                    !enabled || v.trim() ? true : 'Nama template wajib diisi kalau aktif',
                })
              : register('templateName', {
                  validate: (v) =>
                    !enabled || v.trim() ? true : 'Nama template wajib diisi kalau aktif',
                }))}
          />
          {/* Bound to whichever template the selected mode actually sends, so
              two independently approved templates can differ in language. */}
          <Input
            label="Bahasa template"
            placeholder="id"
            hint="Kode bahasa persis seperti di Meta, mis. id atau en. Harus sama dengan yang tertulis di template."
            {...(deliveryMode === 'pdf'
              ? register('pdfTemplateLanguage')
              : register('templateLanguage'))}
          />
        </div>

        <div className="flex items-start gap-2 p-3 rounded bg-canvas/40 border border-line text-caption text-ink-subtle">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          {deliveryMode === 'pdf' ? (
            <span>
              Template PDF harus punya <b>header tipe Document</b> dan <b>3 parameter</b> di
              body: nama customer, nomor invoice, total. Tanpa tombol. Kalau bentuknya beda,
              Meta menolak setiap pengiriman.
            </span>
          ) : (
            <span>
              Template link harus punya <b>3 parameter</b> di body — nama customer, nomor
              invoice, total — plus tombol <b>Visit website</b> dengan URL dinamis. Kalau
              bentuknya beda, Meta menolak setiap pengiriman.
            </span>
          )}
        </div>

        {enabled && !phoneNumberId && (
          <div className="flex items-start gap-2 p-3 rounded bg-warning/5 border border-warning/30 text-small text-warning">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Phone Number ID belum diisi — invoice tidak akan terkirim.</span>
          </div>
        )}

        {enabled && shadowed.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded bg-info/5 border border-info/30 text-small text-info">
            <Info size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              {shadowed.join(', ')} masih aktif, tapi Cloud API punya prioritas
              tertinggi — selama ini aktif, {shadowed.join(', ')} tidak dipakai
              sama sekali (bukan fallback). Matikan kalau sudah tidak dipakai.
            </span>
          </div>
        )}

        <div className="pt-2 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            disabled={!isDirty}
          >
            Simpan konfigurasi
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ModeCard({
  active,
  onClick,
  title,
  description,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`text-left p-3 rounded border transition-colors ${
        active ? 'border-primary bg-primary/5' : 'border-line bg-surface hover:border-primary/30'
      }`}
    >
      <p className="text-small font-medium text-ink">{title}</p>
      <p className="text-caption text-ink-subtle leading-snug mt-0.5">{description}</p>
    </button>
  );
}
